import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { PHASES, STATUS_STYLES, computePhaseProgress } from "../lib/phases";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { CheckCircle2, Circle, MapPin, Calendar, User, MessageSquarePlus, Send, X, FileText } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/jbrp9xqq_equitasfit%20%28Logo%29%20%284%29.png";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function TaskItem({ task, state, comments, token, defaultAuthor, onCommentAdded, openHouseEvents = [] }) {
  const done = !!state?.done;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState(defaultAuthor || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/public/share/${token}/comment`, {
        task_id: task.id,
        text: trimmed,
        sender_name: author.trim(),
      });
      onCommentAdded(data.comment);
      setText("");
      setOpen(false);
      toast.success("Sent to your agent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="flex items-start gap-3" data-testid={`share-task-${task.id}`}>
      {done ? (
        <CheckCircle2 className="h-4 w-4 mt-1 shrink-0" style={{ color: "#010082" }} />
      ) : (
        <Circle className="h-4 w-4 mt-1 shrink-0 text-stone-300" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-sm leading-relaxed ${done ? "text-stone-500" : "text-stone-800"}`}>
              {task.label}
            </p>
            {state?.date && (
              <p className="text-xs text-stone-500 mt-0.5">
                {done ? "Completed by" : "Scheduled for"}{" "}
                {new Date(state.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            {task.subtasks && task.subtasks.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {task.subtasks.map((sub) => {
                  const subDone = !!state?.subtasks?.[sub.id];
                  const subStamp = state?.subtask_completed_at?.[sub.id];
                  return (
                    <li key={sub.id} className="flex items-center gap-2 text-xs">
                      {subDone ? (
                        <CheckCircle2 className="h-3 w-3 shrink-0" style={{ color: "#010082" }} />
                      ) : (
                        <Circle className="h-3 w-3 shrink-0 text-stone-300" />
                      )}
                      <span className={subDone ? "text-stone-500" : "text-stone-700"}>
                        {sub.label}
                        {subDone && subStamp && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400 font-medium tabular-nums">
                            {new Date(subStamp).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {state?.link && (
              <a
                href={state.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-task-link-${task.id}`}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white"
                style={{ backgroundColor: "#010082" }}
              >
                {task.linkLabel === "MLS Printout Link"
                  ? "View MLS Printout"
                  : "View disclosure package"}
                <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
              </a>
            )}
          </div>
          {!open && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid={`share-add-comment-${task.id}`}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#010082] transition-colors"
              aria-label="Leave a note or question"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Leave a note</span>
            </button>
          )}
        </div>

        {comments.length > 0 && (
          <ul className="mt-3 space-y-2 border-l-2 border-stone-100 pl-3">
            {comments.map((c) => (
              <li key={c.id} data-testid={`share-comment-${c.id}`} className="text-xs">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-stone-700">{c.author_name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-400">
                    {formatDate(c.created_at)}
                  </span>
                </div>
                <p className="text-stone-600 leading-relaxed mt-0.5 whitespace-pre-wrap">{c.text}</p>
              </li>
            ))}
          </ul>
        )}

        {task.id === "t5_1" && openHouseEvents.length > 0 && (
          <ul data-testid="share-open-houses" className="mt-3 space-y-2">
            {openHouseEvents.map((e) => {
              const styles = e.status === "completed"
                ? "border-emerald-200 bg-emerald-50/70"
                : e.status === "cancelled"
                ? "border-stone-200 bg-stone-100 opacity-70"
                : "border-[#010082]/25 bg-[#010082]/[0.04]";
              const dateFmt = (() => {
                try {
                  return new Date(e.date + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "long", month: "short", day: "numeric",
                  });
                } catch { return e.date; }
              })();
              return (
                <li
                  key={e.id}
                  data-testid={`share-open-house-${e.id}`}
                  className={`rounded-md border p-3 ${styles}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-xs font-semibold ${e.status === "cancelled" ? "line-through text-stone-500" : "text-stone-900"}`}>
                      {dateFmt}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">
                      {e.status}
                    </span>
                  </div>
                  {e.notes && (
                    <p className="text-xs text-stone-700 leading-relaxed mt-1 whitespace-pre-wrap">{e.notes}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {open && (
          <div className="mt-3 rounded-md border border-stone-200 bg-[#faf7f2] p-3">
            <Textarea
              data-testid={`share-comment-textarea-${task.id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Ask your agent about "${task.label.toLowerCase()}"…`}
              rows={3}
              className="text-sm bg-white border-stone-300"
              autoFocus
            />
            <div className="flex items-center gap-2 mt-2">
              <Input
                data-testid={`share-comment-name-${task.id}`}
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Your name (optional)"
                className="h-8 text-xs bg-white border-stone-300"
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setOpen(false); setText(""); }}
                className="h-8 px-2 text-stone-500"
                aria-label="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                data-testid={`share-comment-send-${task.id}`}
                onClick={submit}
                disabled={!text.trim() || saving}
                className="h-8 px-3 text-xs rounded-full"
                style={{ background: "#010082" }}
              >
                <Send className="h-3.5 w-3.5 mr-1" />
                {saving ? "Sending…" : "Send"}
              </Button>
            </div>
            <p className="text-[10px] text-stone-500 mt-2 leading-relaxed">
              Your agent will get this by email and can reply directly.
            </p>
          </div>
        )}
      </div>
    </li>
  );
}

function getPct(listing) {
  if (!listing?.tasks) return { done: 0, total: 0, pct: 0 };
  const total = PHASES.reduce((s, p) => s + p.tasks.length, 0);
  const done = PHASES.reduce(
    (s, p) => s + p.tasks.filter((t) => listing.tasks?.[t.id]?.done).length,
    0
  );
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

export default function SellerView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useDocumentTitle(data?.property_address);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/share/${token}`);
        setData(res.data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Share link is invalid or inactive.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <img src={LOGO_URL} alt="Equitas" className="h-14 w-14 rounded-full mx-auto shadow-sm ring-1 ring-stone-200" />
          <p className="overline text-stone-500 mt-6 mb-3">Share link</p>
          <h1 className="text-2xl font-heading font-semibold tracking-tight text-stone-900">
            Not available
          </h1>
          <p className="text-sm text-stone-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const overall = getPct(data);

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <header className="border-b border-stone-200 bg-[#faf7f2]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-5xl px-6 py-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="Equitas 1%" className="h-11 w-11 rounded-full object-cover shadow-sm ring-1 ring-stone-200" />
          <div className="flex flex-col leading-none">
            <span className="overline text-stone-500">Equitas Homes</span>
            <span className="text-lg font-heading font-semibold tracking-tight text-stone-900 mt-1">
              Listing Workflow
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
        <div className="mb-4 flex items-center gap-2">
          <p className="overline text-stone-500">For {[data.seller_names, data.seller_2_name].filter(Boolean).join(" & ") || "the seller"}</p>
          <span className="text-stone-300">·</span>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[data.listing_status] || STATUS_STYLES["Pre-Listing"]}`}>
            {data.listing_status}
          </span>
        </div>
        <h1
          data-testid="seller-view-title"
          className="text-4xl sm:text-5xl font-heading font-medium tracking-tight text-stone-900 leading-[1.05]"
        >
          {data.property_address || "Your home"}
        </h1>
        <p className="text-sm text-stone-500 mt-4 max-w-xl">
          A transparent view into every step Equitas Homes is taking to get you to the closing table. Have a question about any step? Tap <span className="inline-flex items-center gap-0.5 text-stone-700"><MessageSquarePlus className="h-3 w-3" /> Leave a note</span> and your agent will get it directly.
        </p>

        <div className="hr-serif my-10" />

        {/* Overall progress */}
        <div className="rounded-md border border-stone-200 bg-white p-7 mb-10">
          <p className="overline text-stone-500 mb-3">Overall progress</p>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-5xl font-heading font-medium tracking-tight text-stone-900 tabular-nums">{overall.pct}</span>
            <span className="text-lg font-heading text-stone-400">%</span>
            <span className="text-sm text-stone-500 tabular-nums ml-auto font-heading">
              {overall.done} of {overall.total} steps complete
            </span>
          </div>
          <Progress value={overall.pct} className="h-1.5 bg-stone-100" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {data.target_list_date && (
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-stone-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Target list date</div>
                  <div className="text-stone-800">{new Date(data.target_list_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
                </div>
              </div>
            )}
            {data.listing_agent && (
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-stone-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Listing agent</div>
                  <div className="text-stone-800">{data.listing_agent}</div>
                </div>
              </div>
            )}
            {data.mls_number && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-stone-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">MLS</div>
                  <div className="text-stone-800 font-mono text-xs">{data.mls_number}</div>
                </div>
              </div>
            )}
            {data.closing_date && (
              <div className="flex items-start gap-2" data-testid="share-closing-date">
                <Calendar className="h-4 w-4 text-emerald-600 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Target close</div>
                  <div className="text-stone-800">{new Date(`${data.closing_date}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {(data.escrow_company || data.escrow_officer_name || data.escrow_officer_email || data.escrow_officer_phone || data.escrow_number || data.seller_signing_date) && (
          <div className="rounded-md border border-stone-200 bg-white p-7 mb-8" data-testid="share-escrow-block">
            <p className="overline text-stone-500 mb-3">Your escrow contact</p>
            <p className="text-xs text-stone-500 leading-relaxed mb-4 max-w-xl">
              Once we&rsquo;re in escrow, this is your neutral third party. They&rsquo;ll hold funds, coordinate signing, and answer any closing questions directly.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {data.escrow_company && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Escrow company</div>
                  <div className="text-stone-800 font-semibold mt-0.5">{data.escrow_company}</div>
                </div>
              )}
              {data.escrow_officer_name && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Escrow officer</div>
                  <div className="text-stone-800 mt-0.5">{data.escrow_officer_name}</div>
                </div>
              )}
              {data.escrow_number && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Escrow #</div>
                  <div className="text-stone-800 font-mono mt-0.5">{data.escrow_number}</div>
                </div>
              )}
              {data.escrow_officer_email && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Email</div>
                  <a
                    href={`mailto:${data.escrow_officer_email}`}
                    className="text-stone-800 hover:text-[#010082] mt-0.5 inline-block break-all"
                  >
                    {data.escrow_officer_email}
                  </a>
                </div>
              )}
              {data.escrow_officer_phone && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Phone</div>
                  <a
                    href={`tel:${data.escrow_officer_phone.replace(/[^0-9+]/g, "")}`}
                    className="text-stone-800 hover:text-[#010082] mt-0.5 inline-block"
                  >
                    {data.escrow_officer_phone}
                  </a>
                </div>
              )}
              {data.seller_signing_date && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Seller signing date</div>
                  <div className="text-stone-800 mt-0.5">
                    {new Date(`${data.seller_signing_date}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: "short", month: "short", day: "numeric", year: "numeric",
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-stone-100">
              <a
                href="https://www.ortconline.com/Web2/downloads/english/Sellers-Checklist.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-seller-signing-checklist"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white"
                style={{ backgroundColor: "#010082" }}
              >
                Seller Signing Checklist
                <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
              </a>
              <p className="text-[11px] text-stone-500 mt-1.5 leading-relaxed">
                A quick prep guide from Old Republic Title on what to bring and expect when you sign.
              </p>
            </div>
          </div>
        )}

        {data.rpa_share_token && (
          <div className="rounded-md border border-[#010082]/20 bg-[#010082]/[0.035] p-7 mb-8" data-testid="share-rpa-block">
            <p className="overline text-stone-500 mb-3">Ratified Purchase Agreement</p>
            <p className="text-xs text-stone-600 leading-relaxed mb-4 max-w-xl">
              This is the fully ratified contract with the buyer, along with all the deadlines your escrow is tracking.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/public/rpa/${data.rpa_share_token}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-rpa-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-xs font-semibold"
              style={{ backgroundColor: "#010082" }}
            >
              View Ratified Purchase Agreement (PDF)
              <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>
            </a>
            {data.rpa_filename && (
              <p className="text-[11px] text-stone-500 mt-2">{data.rpa_filename}</p>
            )}
          </div>
        )}

        {/* Phases */}
        <div className="space-y-4">
          {PHASES.filter((phase) => phase.title !== "Post-Closing").map((phase) => {
            const pp = computePhaseProgress(data, phase.id);
            const idx = String(phase.number).padStart(2, "0");
            const isComplete = pp.total > 0 && pp.done >= pp.total;
            return (
              <div
                key={phase.id}
                data-testid={`share-phase-${phase.id}`}
                data-complete={isComplete ? "true" : "false"}
                className={`rounded-md border p-7 transition-colors ${
                  isComplete
                    ? "border-[#010082]/25 bg-[#010082]/[0.045]"
                    : "border-stone-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-6 mb-5">
                  <div className="flex items-start gap-6">
                    <span className="section-index text-stone-500 text-sm pt-1">Phase {idx}</span>
                    <div>
                      <p className="text-xl font-heading font-semibold tracking-tight text-stone-900 leading-tight">
                        {phase.title}
                      </p>
                      <p className="text-sm text-stone-500 mt-1">{phase.description}</p>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 w-40 shrink-0 pt-2">
                    <Progress value={pp.pct} className="h-1 flex-1 bg-stone-100" />
                    <span className="text-xs tabular-nums text-stone-500 w-10 text-right font-heading">
                      {pp.done}/{pp.total}
                    </span>
                  </div>
                  {isComplete && (
                    <span
                      data-testid={`share-phase-complete-chip-${phase.id}`}
                      className="hidden sm:inline-flex items-center gap-1 rounded-full h-6 px-2.5 text-[10px] font-semibold uppercase tracking-widest text-white shrink-0 self-start mt-2"
                      style={{ backgroundColor: "#010082" }}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Complete
                    </span>
                  )}
                </div>
                <ul className="border-t border-stone-100 pt-4 space-y-4">
                  {phase.tasks.map((task) => {
                    const state = data.tasks?.[task.id];
                    const taskComments = (data.task_comments || []).filter((c) => c.task_id === task.id);
                    const openHouseEvents = task.id === "t5_1"
                      ? (data.open_house_events || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""))
                      : [];
                    return (
                      <TaskItem
                        key={task.id}
                        task={task}
                        state={state}
                        comments={taskComments}
                        token={token}
                        defaultAuthor={data.seller_names || ""}
                        openHouseEvents={openHouseEvents}
                        onCommentAdded={(comment) =>
                          setData((prev) => ({
                            ...prev,
                            task_comments: [...(prev.task_comments || []), comment],
                          }))
                        }
                      />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {(data.activity_notes || []).length > 0 && (
          <section data-testid="share-activity-log" className="mt-14">
            <div className="hr-serif mb-10" />
            <div className="flex items-baseline gap-2 mb-2">
              <FileText className="h-4 w-4 text-stone-500" />
              <h2 className="text-2xl font-heading font-medium tracking-tight text-stone-900">
                Notes from your agent
              </h2>
            </div>
            <p className="text-sm text-stone-500 mb-6 max-w-xl">
              Context, updates and off-checklist observations logged along the way.
            </p>
            <ol className="space-y-4">
              {(data.activity_notes || [])
                .slice()
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((n) => (
                  <li
                    key={n.id}
                    data-testid={`share-activity-note-${n.id}`}
                    className="rounded-md border border-stone-200 bg-white p-6"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <span className="text-sm font-semibold text-stone-900">{n.author_name || "Your agent"}</span>
                      <span className="text-[10px] uppercase tracking-widest text-stone-400">
                        {new Date(n.created_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-[15px] text-stone-700 leading-relaxed whitespace-pre-wrap font-serif italic">
                      {n.text}
                    </p>
                  </li>
                ))}
            </ol>
          </section>
        )}

        <footer className="mt-16 flex items-center justify-between border-t border-stone-200 pt-6 text-xs text-stone-500">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="h-6 w-6 rounded-full" />
            <span>Equitas Homes · 1% List Fee</span>
          </div>
          {data.updated_at && (
            <span>Last updated {new Date(data.updated_at).toLocaleDateString()}</span>
          )}
        </footer>
      </main>
    </div>
  );
}
